import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, TextField, Select, MenuItem, FormControl,
  InputLabel, Grid, Divider, Alert, CircularProgress, Chip, IconButton,
  Stepper, Step, StepLabel, Tooltip, Autocomplete,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { sendMailViaGraph } from '../../utils/graphMailService';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { leesJson, meldFout } from '../../utils/apiFout';
import HandmatigeOnboardingStappen from './HandmatigeOnboardingStappen';
const GRIPP_LOGIN_URL = 'https://full-frame.gripp.com/public/login.php';

const STEPS = ['Excel uploaden', 'Gegevens aanvullen', 'Aanmaken & Mailen'];

const AIRTABLE_FUNCTIES = [
  'Projectleider', 'Editor', 'Cameraman', 'Regisseur', 'Animator',
  'Producer', 'Social Media', 'Strateeg', 'Copywriter', 'IT',
];

const AIRTABLE_TYPES = ['Medewerker', 'Freelancer', 'Stagiair'];

export default function EmployeeOnboarding() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // "Alleen mail sturen" mode
  const [emailOnly, setEmailOnly] = useState(false);
  const [emailOnlyForm, setEmailOnlyForm] = useState({ roepnaam: '', email: '', wachtwoord: '' });
  const [emailOnlySent, setEmailOnlySent] = useState(false);

  // Parsed + editable form data
  const [form, setForm] = useState({
    roepnaam: '', achternaam: '', voorletters: '', geslacht: '',
    emailWerk: '', emailPrive: '', telefoonnummer: '',
    adres: '', straat: '', huisnummer: '', postcode: '', woonplaats: '',
    geboortedatum: '', geboorteplaats: '', nationaliteit: '',
    // NS Business Card-specifiek
    tussenvoegsel: '', toevoeging: '', land: 'NL', personeelsnummer: '',
    telefoonnummerMobiel: '', kostenplaats: '',
    bsn: '', iban: '', idNummer: '',
    functie: '', datumInDienst: '', soortDienstverband: '',
    urenPerWeek: '', dagenPerWeek: '',
    airtableType: 'Medewerker',
    grippRoleId: 9, grippDepartmentId: 9,
    noodcontactNaam: '', noodcontactTelefoon: '', noodcontactRelatie: '',
  });

  // Gripp metadata (roles, departments)
  const [grippMeta, setGrippMeta] = useState({ roles: [], departments: [] });

  // Creation results
  const [results, setResults] = useState(null);

  // Fetch Gripp roles/departments on mount
  useEffect(() => {
    authFetch(`${BACKEND_URL}/Api/onboarding/gripp-meta`)
      .then(r => leesJson(r, 'Gripp-rollen en afdelingen laden'))
      .then(data => setGrippMeta(data))
      .catch(err => { console.warn('Gripp meta fetch failed:', err); meldFout(err, 'Gripp-rollen en afdelingen laden'); });
  }, []);

  // Bestaande Gripp-medewerkers (voor NS-kaart van iemand die al onboard is).
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [empError, setEmpError] = useState(null);
  useEffect(() => {
    setLoadingEmployees(true);
    authFetch(`${BACKEND_URL}/Api/onboarding/employees`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `Fout ${r.status}`);
        const list = d.employees || [];
        setEmployees(list);
        setEmpError(list.length ? null : 'Geen medewerkers uit Gripp ontvangen (leeg antwoord).');
      })
      .catch((err) => setEmpError(`Kon medewerkers niet laden: ${err.message}`))
      .finally(() => setLoadingEmployees(false));
  }, []);

  const updateForm = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // 📇 NS Business Card (.xls) genereren uit de ingevulde gegevens.
  const [nsBusy, setNsBusy] = useState(false);
  const downloadNsCard = async () => {
    if (!form.achternaam) { toast.error('Vul minstens de achternaam in.'); return; }
    setNsBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/onboarding/ns-business-card`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Fout ${r.status}`); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `NS-BusinessCard-${(form.roepnaam || '')}_${form.achternaam}.xls`.replace(/\s+/g, '_');
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('NS Business Card gegenereerd — upload het bestand op de NS-site.');
    } catch (e) { toast.error(`Genereren mislukt: ${e.message}`); }
    finally { setNsBusy(false); }
  };

  // ── Step 1: File Upload & Parse ─────────────────────────────────
  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await authFetch(`${BACKEND_URL}/Api/onboarding/parse-excel`, {
        method: 'POST', body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      const p = data.parsed;
      setForm(prev => ({
        ...prev,
        roepnaam: p.roepnaam || prev.roepnaam,
        achternaam: p.achternaam || prev.achternaam,
        voorletters: p.voorletters || prev.voorletters,
        geslacht: p.geslacht || prev.geslacht,
        emailPrive: p.emailPrive || prev.emailPrive,
        telefoonnummer: p.telefoonnummer || prev.telefoonnummer,
        adres: p.adres || prev.adres,
        straat: p.straat || prev.straat,
        huisnummer: p.huisnummer || prev.huisnummer,
        postcode: p.postcode || prev.postcode,
        geboortedatum: p.geboortedatum || prev.geboortedatum,
        geboorteplaats: p.geboorteplaats || prev.geboorteplaats,
        nationaliteit: p.nationaliteit || prev.nationaliteit,
        bsn: p.bsn || prev.bsn,
        iban: p.iban || prev.iban,
        idNummer: p.idNummer || prev.idNummer,
        functie: p.functie || prev.functie,
        datumInDienst: p.datumInDienst || prev.datumInDienst,
        soortDienstverband: p.soortDienstverband || prev.soortDienstverband,
        urenPerWeek: p.urenPerWeek || prev.urenPerWeek,
        dagenPerWeek: p.dagenPerWeek || prev.dagenPerWeek,
        noodcontactNaam: p.noodcontactNaam || prev.noodcontactNaam,
        noodcontactTelefoon: p.noodcontactTelefoon || prev.noodcontactTelefoon,
        noodcontactRelatie: p.noodcontactRelatie || prev.noodcontactRelatie,
        // Auto-generate werk email
        emailWerk: prev.emailWerk || (p.roepnaam ? `${p.roepnaam.toLowerCase()}@fullframe.nl` : ''),
      }));

      toast.success(`Excel geparsed: ${p.roepnaam} ${p.achternaam}`);
      setStep(1);
    } catch (err) {
      toast.error(`Parse fout: ${err.message}`);
    } finally {
      setParsing(false);
    }
  };

  // ── Step 2: Validation ──────────────────────────────────────────
  const missingFields = [];
  if (!form.roepnaam) missingFields.push('Roepnaam');
  if (!form.achternaam) missingFields.push('Achternaam');
  if (!form.emailWerk) missingFields.push('Werk e-mail');
  if (!form.functie) missingFields.push('Functie');
  if (!form.datumInDienst) missingFields.push('Datum in dienst');

  const canCreate = form.roepnaam && form.achternaam && form.emailWerk;

  // ── Step 3: Create ─────────────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    try {
      const resp = await authFetch(`${BACKEND_URL}/Api/onboarding/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: form }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      setResults(data.results);
      setStep(2);

      if (data.results.airtable?.success && data.results.gripp?.success) {
        toast.success('Medewerker aangemaakt in Airtable + Gripp!');
      } else {
        toast('Gedeeltelijk gelukt, check de resultaten', { icon: '⚠️' });
      }
    } catch (err) {
      toast.error(`Aanmaken mislukt: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  // ── Email ──────────────────────────────────────────────────────
  const handleSendEmail = async () => {
    if (!results?.gripp?.success) return;
    if (!(await bevestig({ titel: 'Welkomstmail versturen?', tekst: `De mail met Gripp-inloggegevens gaat naar ${form.emailWerk}.`, knop: 'Versturen' }))) return;
    setSendingEmail(true);
    try {
      const html = buildWelcomeEmailHtml(form, results.gripp);
      await sendMailViaGraph(instance, account, {
        to: form.emailWerk,
        subject: `Welkom bij Full Frame, ${form.roepnaam}! — Je Gripp inloggegevens`,
        body: html,
      });
      toast.success(`Welkomstmail verzonden naar ${form.emailWerk}`);
    } catch (err) {
      toast.error(`Email verzenden mislukt: ${err.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Email-only mode ─────────────────────────────────────────
  const handleSendEmailOnly = async () => {
    const { roepnaam, email, wachtwoord } = emailOnlyForm;
    if (!roepnaam || !email || !wachtwoord) return;
    if (!(await bevestig({ titel: 'Welkomstmail versturen?', tekst: `De mail met Gripp-inloggegevens gaat naar ${email}.`, knop: 'Versturen' }))) return;
    setSendingEmail(true);
    try {
      const html = buildWelcomeEmailHtml(
        { roepnaam, emailWerk: email },
        { tempPassword: wachtwoord }
      );
      await sendMailViaGraph(instance, account, {
        to: email,
        subject: `Welkom bij Full Frame, ${roepnaam}! — Je Gripp inloggegevens`,
        body: html,
      });
      toast.success(`Welkomstmail verzonden naar ${email}`);
      setEmailOnlySent(true);
    } catch (err) {
      toast.error(`Email verzenden mislukt: ${err.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setFile(null);
    setResults(null);
    setForm({
      roepnaam: '', achternaam: '', voorletters: '', geslacht: '',
      emailWerk: '', emailPrive: '', telefoonnummer: '',
      adres: '', straat: '', huisnummer: '', postcode: '', woonplaats: '',
      geboortedatum: '', geboorteplaats: '', nationaliteit: '',
      bsn: '', iban: '', idNummer: '',
      functie: '', datumInDienst: '', soortDienstverband: '',
      urenPerWeek: '', dagenPerWeek: '',
      airtableType: 'Medewerker',
      grippRoleId: 9, grippDepartmentId: 9,
      noodcontactNaam: '', noodcontactTelefoon: '', noodcontactRelatie: '',
    });
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Box>
      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {/* ── STEP 0: Upload ── */}
      {step === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>Mutatieformulier uploaden</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload het ingevulde Excel mutatieformulier om de gegevens automatisch in te lezen.
          </Typography>

          {file ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <FileIcon color="primary" />
              <Typography>{file.name}</Typography>
              <IconButton size="small" onClick={() => setFile(null)}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              sx={{ mb: 2 }}
            >
              Selecteer Excel bestand
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileSelect} />
            </Button>
          )}

          <Box>
            <Button
              variant="contained"
              onClick={handleParse}
              disabled={!file || parsing}
              startIcon={parsing ? <CircularProgress size={18} /> : null}
            >
              {parsing ? 'Bezig met parsen...' : 'Inlezen'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="text" onClick={() => setStep(1)}>
              Handmatig invullen
            </Button>
            <Button variant="text" onClick={() => setStep(1)}>
              📇 NS-kaart bestaande medewerker
            </Button>
            <Button variant="text" startIcon={<EmailIcon />} onClick={() => setEmailOnly(true)}>
              Alleen mail sturen
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── EMAIL ONLY MODE ── */}
      {step === 0 && emailOnly && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>Welkomstmail sturen</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stuur Gripp inloggegevens naar een medewerker die al is aangemaakt.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Roepnaam" value={emailOnlyForm.roepnaam}
                onChange={e => setEmailOnlyForm(prev => ({ ...prev, roepnaam: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="E-mailadres" value={emailOnlyForm.email}
                onChange={e => setEmailOnlyForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="naam@fullframe.nl" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Tijdelijk wachtwoord" value={emailOnlyForm.wachtwoord}
                onChange={e => setEmailOnlyForm(prev => ({ ...prev, wachtwoord: e.target.value }))} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={sendingEmail ? <CircularProgress size={18} /> : <SendIcon />}
              onClick={handleSendEmailOnly}
              disabled={sendingEmail || !emailOnlyForm.roepnaam || !emailOnlyForm.email || !emailOnlyForm.wachtwoord}
            >
              {sendingEmail ? 'Verzenden...' : 'Welkomstmail versturen'}
            </Button>
            <Button variant="text" onClick={() => { setEmailOnly(false); setEmailOnlySent(false); }}>
              Annuleren
            </Button>
            {emailOnlySent && <Chip icon={<CheckIcon />} label="Verzonden!" color="success" size="small" />}
          </Box>
        </Paper>
      )}

      {/* ── STEP 1: Form ── */}
      {step === 1 && (
        <Box>
          {missingFields.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Nog in te vullen: {missingFields.join(', ')}
            </Alert>
          )}

          {/* Persoonlijke gegevens */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Persoonlijke gegevens</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Roepnaam *" value={form.roepnaam}
                  onChange={e => updateForm('roepnaam', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Achternaam *" value={form.achternaam}
                  onChange={e => updateForm('achternaam', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Voorletters" value={form.voorletters}
                  onChange={e => updateForm('voorletters', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Geslacht" value={form.geslacht}
                  onChange={e => updateForm('geslacht', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Geboortedatum" type="date" value={form.geboortedatum}
                  onChange={e => updateForm('geboortedatum', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Geboorteplaats" value={form.geboorteplaats}
                  onChange={e => updateForm('geboorteplaats', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* Contact */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Contact & Adres</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Werk e-mail *" value={form.emailWerk}
                  onChange={e => updateForm('emailWerk', e.target.value)}
                  helperText="Wordt gebruikt als login voor Gripp" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Privé e-mail" value={form.emailPrive}
                  onChange={e => updateForm('emailPrive', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Telefoonnummer" value={form.telefoonnummer}
                  onChange={e => updateForm('telefoonnummer', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Straat" value={form.straat}
                  onChange={e => updateForm('straat', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField fullWidth size="small" label="Huisnr" value={form.huisnummer}
                  onChange={e => updateForm('huisnummer', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Postcode" value={form.postcode}
                  onChange={e => updateForm('postcode', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Woonplaats" value={form.woonplaats}
                  onChange={e => updateForm('woonplaats', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* Financieel */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Financieel & ID</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="BSN" value={form.bsn}
                  onChange={e => updateForm('bsn', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="IBAN" value={form.iban}
                  onChange={e => updateForm('iban', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="ID-bewijs nummer" value={form.idNummer}
                  onChange={e => updateForm('idNummer', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* NS Business Card */}
          <Paper sx={{ p: 2.5, mb: 2, border: '1px solid #ffe08a', bgcolor: '#fffdf5' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>📇 NS Business Card</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Vult automatisch de NS-template in met bovenstaande gegevens (naam, adres, geboortedatum, e-mail).
              Vul hieronder de NS-specifieke velden aan en download het .xls om op de NS-site te uploaden.
              <b> Voor iemand die al onboard is:</b> kies hieronder de medewerker — adres/geboortedatum/e-mail
              worden uit Gripp voorgevuld.
            </Typography>
            <Autocomplete
              size="small"
              options={employees}
              loading={loadingEmployees}
              sx={{ mb: 2 }}
              getOptionLabel={(o) => (o?.name || '') + (o?.email ? ` — ${o.email}` : '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(_e, v) => {
                if (v?.fill) {
                  setForm(prev => ({ ...prev, ...v.fill }));
                  toast.success(`Gegevens van ${v.name} ingevuld uit Gripp.`);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Bestaande medewerker (uit Gripp) — vult gegevens in"
                  placeholder="typ een naam…"
                  error={!!empError}
                  helperText={empError || 'Geen match nodig — je kunt de velden ook handmatig invullen en downloaden.'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmployees ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }} />
              )}
            />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" label="Tussenvoegsel" value={form.tussenvoegsel}
                  onChange={e => updateForm('tussenvoegsel', e.target.value)} placeholder="van den" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" label="Toevoeging (huisnr.)" value={form.toevoeging}
                  onChange={e => updateForm('toevoeging', e.target.value)} placeholder="a / bis" />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField fullWidth size="small" label="Land" value={form.land}
                  onChange={e => updateForm('land', e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" label="Personeelsnummer" value={form.personeelsnummer}
                  onChange={e => updateForm('personeelsnummer', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Telefoon mobiel" value={form.telefoonnummerMobiel}
                  onChange={e => updateForm('telefoonnummerMobiel', e.target.value)} placeholder="06…" />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth size="small" label="Kostenplaats" value={form.kostenplaats}
                  onChange={e => updateForm('kostenplaats', e.target.value)} placeholder="max 40 tekens" inputProps={{ maxLength: 40 }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button fullWidth variant="contained" onClick={downloadNsCard} disabled={nsBusy}
                  startIcon={nsBusy ? <CircularProgress size={16} color="inherit" /> : <FileIcon />}
                  sx={{ height: 40, textTransform: 'none', fontWeight: 700, bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}>
                  Download .xls
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Dienstverband */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Dienstverband</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Functie *</InputLabel>
                  <Select value={form.functie} label="Functie *"
                    onChange={e => updateForm('functie', e.target.value)}>
                    {AIRTABLE_FUNCTIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Datum in dienst *" type="date" value={form.datumInDienst}
                  onChange={e => updateForm('datumInDienst', e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Soort dienstverband" value={form.soortDienstverband}
                  onChange={e => updateForm('soortDienstverband', e.target.value)}
                  placeholder="Bijv. bepaald, onbepaald" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Uren/week" value={form.urenPerWeek}
                  onChange={e => updateForm('urenPerWeek', e.target.value)} type="number" />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Dagen/week" value={form.dagenPerWeek}
                  onChange={e => updateForm('dagenPerWeek', e.target.value)} type="number" />
              </Grid>
            </Grid>
          </Paper>

          {/* Systeem-instellingen */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Systeem-instellingen</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Airtable Type</InputLabel>
                  <Select value={form.airtableType} label="Airtable Type"
                    onChange={e => updateForm('airtableType', e.target.value)}>
                    {AIRTABLE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gripp Rol</InputLabel>
                  <Select value={form.grippRoleId} label="Gripp Rol"
                    onChange={e => updateForm('grippRoleId', e.target.value)}>
                    {grippMeta.roles.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
                    {grippMeta.roles.length === 0 && <MenuItem value={9}>Projectleider (standaard)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gripp Afdeling</InputLabel>
                  <Select value={form.grippDepartmentId} label="Gripp Afdeling"
                    onChange={e => updateForm('grippDepartmentId', e.target.value)}>
                    {grippMeta.departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                    {grippMeta.departments.length === 0 && <MenuItem value={9}>Afdeling Projectleider (standaard)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={() => setStep(0)}>Terug</Button>
            <Button
              variant="contained"
              startIcon={creating ? <CircularProgress size={18} /> : <PersonAddIcon />}
              onClick={handleCreate}
              disabled={!canCreate || creating}
            >
              {creating ? 'Aanmaken...' : 'Aanmaken in Airtable + Gripp'}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── STEP 2: Results ── */}
      {step === 2 && results && (
        <Box>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Resultaat</Typography>

            {/* Airtable */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              {results.airtable?.success
                ? <CheckIcon color="success" />
                : <ErrorIcon color="error" />}
              <Typography fontWeight={600}>Airtable</Typography>
              {results.airtable?.success
                ? <Chip label={`Record: ${results.airtable.recordId}`} size="small" color="success" variant="outlined" />
                : <Chip label={results.airtable?.error} size="small" color="error" variant="outlined" />}
            </Box>

            {/* Gripp */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {results.gripp?.success
                ? <CheckIcon color="success" />
                : <ErrorIcon color="error" />}
              <Typography fontWeight={600}>Gripp</Typography>
              {results.gripp?.success
                ? <Chip label={`Employee ID: ${results.gripp.employeeId}`} size="small" color="success" variant="outlined" />
                : <Chip label={results.gripp?.error} size="small" color="error" variant="outlined" />}
            </Box>

            {results.gripp?.success && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Gripp Inloggegevens</Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem', mb: 2 }}>
                  <div><strong>URL:</strong> {GRIPP_LOGIN_URL}</div>
                  <div><strong>Gebruikersnaam:</strong> {form.emailWerk}</div>
                  <div><strong>Wachtwoord:</strong> {results.gripp.tempPassword}</div>
                </Box>

                <Tooltip title={`Stuurt mail naar ${form.emailWerk} via jouw Outlook`}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={sendingEmail ? <CircularProgress size={18} /> : <SendIcon />}
                    onClick={handleSendEmail}
                    disabled={sendingEmail}
                  >
                    {sendingEmail ? 'Verzenden...' : `Welkomstmail sturen naar ${form.roepnaam}`}
                  </Button>
                </Tooltip>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Vergeet niet de medewerker te activeren in Gripp (staat standaard op inactief).
                </Alert>
                <HandmatigeOnboardingStappen email={form.emailWerk} naam={form.roepnaam} />
              </>
            )}
          </Paper>

          <Button variant="outlined" onClick={handleReset}>
            Nieuwe medewerker
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ── Email template ──────────────────────────────────────────────
function buildWelcomeEmailHtml(form, grippResult) {
  return `
  <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #1a1a2e, #16213e); color: white; padding: 24px; border-radius: 12px 12px 0 0;">
      <h2 style="margin: 0;">Welkom bij Full Frame!</h2>
    </div>
    <div style="background: #f8f9fa; padding: 24px; border-radius: 0 0 12px 12px; border: 1px solid #dee2e6; border-top: none;">
      <p>Hoi ${form.roepnaam},</p>
      <p>Leuk dat je bij ons komt werken! Er is een Gripp-account voor je aangemaakt.
         Gripp is ons projectmanagement systeem waar je je uren schrijft en projecten beheert.</p>

      <table style="border-collapse: collapse; margin: 20px 0; width: 100%; background: white; border-radius: 8px; overflow: hidden;">
        <tr>
          <td style="padding: 12px 16px; background: #e9ecef; font-weight: bold; border: 1px solid #dee2e6; width: 140px;">Inlog URL</td>
          <td style="padding: 12px 16px; border: 1px solid #dee2e6;">
            <a href="${GRIPP_LOGIN_URL}" style="color: #0066cc;">${GRIPP_LOGIN_URL}</a>
          </td>
        </tr>
        <tr>
          <td style="padding: 12px 16px; background: #e9ecef; font-weight: bold; border: 1px solid #dee2e6;">Gebruikersnaam</td>
          <td style="padding: 12px 16px; border: 1px solid #dee2e6;">${form.emailWerk}</td>
        </tr>
        <tr>
          <td style="padding: 12px 16px; background: #e9ecef; font-weight: bold; border: 1px solid #dee2e6;">Wachtwoord</td>
          <td style="padding: 12px 16px; border: 1px solid #dee2e6; font-family: monospace; font-size: 15px; letter-spacing: 1px;">${grippResult.tempPassword}</td>
        </tr>
      </table>

      <p style="color: #dc3545; font-weight: bold;">Wijzig je wachtwoord na de eerste keer inloggen!</p>

      <p>Mocht je vragen hebben, neem dan gerust contact op.</p>
      <p style="margin-top: 24px;">Groetjes,<br><strong>Full Frame</strong></p>
    </div>
  </div>`;
}
